import type { AnimationI, EaseT, SimpleStyleT } from "tweenly"
import gsap from "gsap"
import GSDevTools from "gsap/GSDevTools"
import { set } from "lodash"
import { RefObject } from "react"
gsap.registerPlugin(GSDevTools)

type SerializedVars = GSAPTweenVars & { [key: string]: any }
type ValidGSAPTarget = gsap.TweenTarget
export const serializeAnimationStateForPreview = (
    animationStates: SimpleStyleT[]
): SerializedVars => {
    return animationStates.reduce((acc, state) => {
        const value = state.unit === "%" ? `${state.value}%` : state.value
        set(acc, state.property, value)
        return acc
    }, {} as SerializedVars)
}

export const serializeEaseForPreview = (ease: EaseT): string | undefined => {
    let result: string = ""

    if (!ease) return undefined

    result = ease.easeStyle + "." + ease.easeType
    const strength = ease.easeStrengthValues.find((s) => s.strength === ease.easeStrength)
    if (strength) result += strength.value
    return result
}

const applyTween = (
    target: ValidGSAPTarget,
    animation: AnimationI,
    engine: typeof gsap | gsap.core.Timeline
): GSAPTween | gsap.core.Timeline => {
    const { tween, delay, duration, ease } = animation

    // serialized values
    const fromVars = serializeAnimationStateForPreview(tween.from)
    const toVars = serializeAnimationStateForPreview(tween.to)
    const commonVars = {
        duration,
        ease: serializeEaseForPreview(ease),
    }

    switch (tween.type) {
        case "fromTo":
            return engine.fromTo(target, fromVars, { ...toVars, ...commonVars }, delay)
        case "from":
            return engine.from(target, { ...fromVars, ...commonVars }, delay)
        case "to":
            return engine.to(target, { ...toVars, ...commonVars }, delay)
        case "set":
            // NOTE: master image-sequence frames no longer flow through here — they are
            // emitted as `data-tw-seq` markers + painted by the decode-gated sequence
            // player (see tweenly's sequencePlayer / masterTimeline.slice.ts).
            return engine.set(target, fromVars, delay)
        default:
            return engine.to(target, { duration: 0 })
    }
}

/**
 * Who a tween on the master timeline belongs to.
 *
 * Written into GSAP's `data` slot (which exists for exactly this purpose) so that the tweens
 * of one object can be found and replaced without rebuilding the whole timeline — see
 * `updateObjectTweens`.
 */
export type TweenOwnerT = {
    objectId: string
    animationId: string
}

export const getTweenOwner = (tween: { data?: unknown }): TweenOwnerT | undefined => {
    const data = tween.data as TweenOwnerT | undefined
    return data && typeof data.objectId === "string" ? data : undefined
}

export const initTweenForPreview = (
    animation: AnimationI,
    getRef: (key: string) => RefObject<unknown> | undefined
): GSAPTween => {
    const owner: TweenOwnerT = { objectId: animation.objectId, animationId: animation.id }
    const element = getRef(animation.objectId)?.current

    if (!element) {
        console.warn(`Target for objectId ${animation.objectId} not found.`)
        // tagged as well: the placeholder keeps the timeline's shape, so a targeted update
        // has to be able to find and drop it too
        const placeholder = gsap.to({}, { duration: 0 })
        placeholder.data = owner
        return placeholder
    }

    const gsapTween = applyTween(element as ValidGSAPTarget, animation, gsap) as GSAPTween
    gsapTween.data = owner

    if (animation.repeat > 0) {
        gsapTween.repeat(animation.repeat)
    }
    return gsapTween
}

export const initSubTimelineTween = (
    animation: AnimationI,
    objectId: string,
    timeline: gsap.core.Timeline
): gsap.core.Timeline => {
    return applyTween(`#${objectId}`, animation, timeline) as gsap.core.Timeline
}
